import { GlobalHelperFunctions } from '@/common/GlobalHelperFunctions';
import { getProformaEmailTemplates } from '@/services/api/api-calls/getProformaEmailTemplates';
import { getAgencyProformas } from '@/services/api/api-calls/getAgencyProformas';
import { getPortcallProformas } from '@/services/api/api-calls/getPortcallProformas';
import { getProforma } from '@/services/api/api-calls/getProforma';
import { updateProforma } from '@/services/api/api-calls/updateProfoma';
import { usePortcallStore } from '@/store/portcallStore';
import { ChangeParams } from '@/types/ChangeParams';
import { GatResponseResultCategory } from '@/types/GatResponseResult';
import type { EnquiryListItem, ProformaListItem, ProformaEmailTemplateItem, ProformaCurrency, ProformaWindow } from '@/types/ProformaTypes';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useApplicationStore } from '@/store/applicationStore';
import { recalculateProforma } from '../utils/proforma-utils';

export const useProformaStore = defineStore('proforma-store', () => {
  const portcallStore = usePortcallStore();
  const applicationStore = useApplicationStore();
  const agencyId = computed(() => applicationStore.agencyIdSelected || 0);
  const portcallAgencyId = computed(() => portcallStore.callDetails.SETUP_ID);
  const agencyProformas = ref<EnquiryListItem[]>([]);
  const portcallProformas = ref<ProformaListItem[]>([]);
  const emailTemplates = ref<ProformaEmailTemplateItem[]>([]);
  const currencies = ref<ProformaCurrency[]>([]);
  const proformaWindow = ref<ProformaWindow | undefined>();
  const orgProformaWindow = ref<ProformaWindow | undefined>();
  const proformaDetailsLoading = ref(true);
  const proformaDetails = ref<any>({ empty: true, extra: {}, tabsHasData: {} });

  function setProformaDetailsLoading(value: boolean) {
    proformaDetailsLoading.value = value;
  }

  function setProformaDetails(data: any) {
    proformaDetails.value = data;
    setProformaDetailsLoading(false);
  }

  async function fetchAgencyProformas(fromDate: string, toDate: string, includeClosed: boolean) {
    const proformasResult = await getAgencyProformas(agencyId.value, fromDate, toDate, includeClosed);

    if (proformasResult.resultCategory === GatResponseResultCategory.Success && proformasResult.payload) {
      agencyProformas.value = proformasResult.payload.proformas;
    } else {
      agencyProformas.value = [];
    }

    emailTemplates.value = await getProformaEmailTemplates(agencyId.value, false);
  }

  async function fetchPortcallProformas(portcallId: string) {
    const proformasResult = await getPortcallProformas(portcallId);

    if (proformasResult.resultCategory === GatResponseResultCategory.Success && proformasResult.payload) {
      portcallProformas.value = proformasResult.payload.proformas;
    } else {
      portcallProformas.value = [];
    }

    emailTemplates.value = await getProformaEmailTemplates(portcallAgencyId.value, true);
  }

  async function fetchProformaWindow(proformaId: number | string) {
    if (proformaId) {
      setProformaDetailsLoading(true);
      const mappedResult = await getProforma(proformaId);
      const proformaWindowResult = recalculateProforma(
        mappedResult.proformaLines,
        mappedResult.currencies,
        mappedResult.proformaInfo
      );
      proformaWindow.value = GlobalHelperFunctions.duplicateViaJson(proformaWindowResult);
      orgProformaWindow.value = GlobalHelperFunctions.duplicateViaJson(proformaWindowResult);
      setProformaDetailsLoading(false);
    }
  }

  async function updateProformaWindow(proformaId: string, payload: ChangeParams<ProformaWindow>) {
    const response = await updateProforma(proformaId, payload);
    if (response.resultCategory === GatResponseResultCategory.Success) {
      orgProformaWindow.value = GlobalHelperFunctions.duplicateViaJson(proformaWindow.value);
    }
    return response;
  }

  return {
    agencyProformas,
    portcallProformas,
    proformaWindow,
    orgProformaWindow,
    emailTemplates,
    currencies,
    fetchAgencyProformas,
    fetchPortcallProformas,
    fetchProformaWindow,
    updateProformaWindow,
    setProformaDetailsLoading,
    setProformaDetails,
  };
});
